import { Config } from './config.types';

const APP_CONFIG_STORAGE_KEY = 'app_config';
const APP_CONFIG_VERSION_KEY = 'app_config_version';

/**
 * Get environment variable with fallback
 */
const getEnvVar = (key: string, fallback: string = ''): string => {
  return import.meta.env[key] || fallback;
};

/**
 * Determine the current environment
 */
const getEnvironment = () => {
  // Check for manual override first (useful for testing)
  const manualOverride = localStorage.getItem('app-environment-override');
  if (manualOverride === 'dev' || manualOverride === 'prod') {
    console.log(`Using manual environment override: ${manualOverride}`);
    return manualOverride;
  }

  // Use Vite's built-in environment detection
  const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production';
  return isProduction ? 'prod' : 'dev';
};

/**
 * Build configuration from environment variables
 * Values come from .env.development or .env.production based on build mode
 */
export const config: Config = {
  environment: getEnvironment(),
  api: {
    baseUrl: getEnvVar('VITE_API_BASE_URL', 'https://localhost:44374')
  },
  auth: {
    authority: getEnvVar('VITE_AUTH_AUTHORITY', 'https://localhost:44374/'),
    clientId: getEnvVar('VITE_AUTH_CLIENT_ID', 'Routemize_App'),
    scope: getEnvVar('VITE_AUTH_SCOPE', 'openid profile email offline_access Routemize')
  },
  chargebee: {
    site: getEnvVar('VITE_CHARGEBEE_SITE', 'routemize-test'),
    publishableKey: getEnvVar('VITE_CHARGEBEE_KEY', 'test_hfD10oEX82fLfFGN0enlDJeKaQ750UH8')
  },
  googleMapsApiKey: getEnvVar('VITE_GOOGLE_MAPS_API_KEY', 'AIzaSyAg-VKD17JyUIn13AxMQ5mZo41GK-5x2L8'),
  anthropic: {
    // Prototype only — calling Anthropic directly from the browser exposes this
    // key. For production, proxy through a backend endpoint instead.
    apiKey: getEnvVar('VITE_ANTHROPIC_API_KEY', ''),
    model: getEnvVar('VITE_ANTHROPIC_MODEL', 'claude-sonnet-4-5-20250929')
  },
  routy: {
    // Master switch. Defaults OFF (fail-closed) so the feature is hidden in
    // production and any build that doesn't explicitly opt in; only .env.development
    // turns it on for the browser prototype. Keeps `main` deployable while the
    // backend agent is built.
    enabled: getEnvVar('VITE_ROUTY_ENABLED', 'false') === 'true',
    // Flip to 'true' once the backend Routy agent is live (see
    // docs/implementation/routy-assistant-backend-spec.md). Until then the
    // browser prototype loop is used.
    useBackend: getEnvVar('VITE_ROUTY_USE_BACKEND', 'false') === 'true'
  }
};

/**
 * Manually override the environment (useful for testing production config locally)
 * This requires a page reload to take effect
 */
export const setEnvironmentOverride = (env: 'dev' | 'prod' | null) => {
  // Clear stored config when environment changes
  localStorage.removeItem(APP_CONFIG_STORAGE_KEY);
  localStorage.removeItem(APP_CONFIG_VERSION_KEY);

  if (env === null) {
    localStorage.removeItem('app-environment-override');
    console.log('Removed environment override - will use automatic detection');
  } else {
    localStorage.setItem('app-environment-override', env);
    console.log(`Set environment override to: ${env}`);
  }

  window.location.reload();
};

/**
 * Get the current environment
 */
export const getCurrentEnvironment = () => {
  return config.environment;
};

// Log current configuration on load (non-sensitive info only)
if (import.meta.env.DEV) {
  console.log(`Routemize running in ${config.environment} mode`);
  console.log(`API: ${config.api.baseUrl}`);
  console.log(`Auth: ${config.auth.authority}`);
}
